import {
  BYTE_ORDER_MARK,
  CSV_DELIMITER,
  CSV_ESCAPED_QUOTE,
  CSV_NEWLINE,
  CSV_QUOTE,
} from './catalog';
import { EXPORT_ISSUE, issueFor } from './issue';
import type { ExportIssue } from './issue';
import { fail, ok } from './result';
import type { ExportResult } from './result';

/**
 * RFC 4180 CSV, written and read by hand.
 *
 * The format is small enough that a dependency would cost more than it saves —
 * and the one thing this codec must never do is *guess*. A file whose quotes do
 * not close, or whose rows do not line up, is refused with a reason; the caller
 * is never handed a half-parsed document that looks plausible.
 *
 * The dialect, fixed here and documented in `IMPORT_EXPORT.md`:
 *
 * - UTF-8, comma-delimited, CRLF line endings.
 * - A field is quoted when it holds a comma, a quote, CR or LF. Quotes inside
 *   are doubled.
 * - A leading or trailing space is significant and is never trimmed.
 * - A byte-order mark at the start is stripped, because Excel writes one.
 * - A blank line is skipped. A line of nothing but delimiters is a row of empty
 *   fields, and is not blank.
 */

/** One parsed row, as its fields. */
export type CsvRow = readonly string[];

/** True when the field has to be quoted to survive the round trip. */
const needsQuoting = (field: string): boolean =>
  field.includes(CSV_DELIMITER) ||
  field.includes(CSV_QUOTE) ||
  field.includes('\r') ||
  field.includes('\n');

/** One field, quoted if it has to be. */
const encodeField = (field: string): string =>
  needsQuoting(field)
    ? `${CSV_QUOTE}${field.split(CSV_QUOTE).join(CSV_ESCAPED_QUOTE)}${CSV_QUOTE}`
    : field;

/** One row, delimited. */
export const encodeCsvRow = (row: CsvRow): string =>
  row.map((field) => encodeField(field)).join(CSV_DELIMITER);

/**
 * Rows as a CSV document, CRLF-terminated including the last line.
 *
 * A trailing newline is written on purpose: it is what every spreadsheet and
 * every `diff` expects, and it makes appending a hand-written row safe.
 */
export const encodeCsv = (rows: readonly CsvRow[]): string =>
  rows.map((row) => `${encodeCsvRow(row)}${CSV_NEWLINE}`).join('');

/** A row that holds nothing at all — skipped rather than read as one empty field. */
const isBlank = (row: CsvRow): boolean => row.length === 1 && row[0] === '';

/**
 * Parses a CSV document.
 *
 * The scanner walks the text once, tracking whether it is inside a quoted field,
 * so an embedded newline or comma cannot split a row. Anything left open at the
 * end of the file is an issue, not a partially-read row.
 */
export const parseCsv = (text: string): ExportResult<readonly CsvRow[]> => {
  const source = text.startsWith(BYTE_ORDER_MARK) ? text.slice(BYTE_ORDER_MARK.length) : text;
  const rows: CsvRow[] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let index = 0;

  while (index < source.length) {
    const character = source[index] ?? '';

    if (quoted) {
      if (character === CSV_QUOTE) {
        // A doubled quote is one literal quote; a lone one closes the field.
        if (source[index + 1] === CSV_QUOTE) {
          field += CSV_QUOTE;
          index += 2;
          continue;
        }
        quoted = false;
        index += 1;
        continue;
      }
      field += character;
      index += 1;
      continue;
    }

    if (character === CSV_QUOTE) {
      quoted = true;
      index += 1;
      continue;
    }

    if (character === CSV_DELIMITER) {
      row.push(field);
      field = '';
      index += 1;
      continue;
    }

    if (character === '\r' || character === '\n') {
      // CRLF counts as one break; a lone CR is one too, as RFC 4180 allows.
      const step = character === '\r' && source[index + 1] === '\n' ? 2 : 1;
      row.push(field);
      if (!isBlank(row)) rows.push(row);
      row = [];
      field = '';
      index += step;
      continue;
    }

    field += character;
    index += 1;
  }

  if (quoted) {
    return fail(issueFor(EXPORT_ISSUE.MALFORMED_CSV, 'a quoted field is never closed'));
  }

  // The last line may or may not have ended in a newline; either way it is a row.
  if (field !== '' || row.length > 0) {
    row.push(field);
    if (!isBlank(row)) rows.push(row);
  }

  return ok(rows);
};

/** The first issue of a failed parse, for a caller that only wants to report it. */
export const issueOf = (result: ExportResult<unknown>): ExportIssue | null =>
  result.ok ? null : result.issue;
